import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const field =
  "w-full bg-[#0A0A0A] border border-[#27272A] rounded-sm px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-[#6b6b6b] outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#D4AF37] transition-colors mb-8" data-testid="login-back">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="text-[#D4AF37]" size={22} />
          <span className="font-display text-2xl">Angel Desk</span>
        </div>
        <p className="text-[#A3A3A3] text-sm mb-8">Staff sign-in for Fallen Angels Entertainment.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-1.5 block">Email</label>
            <input className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#A3A3A3] mb-1.5 block">Password</label>
            <input className={field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-[#0A0A0A] font-semibold py-3 rounded-sm hover:bg-[#F3E5AB] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px flex-1 bg-[#27272A]" />
          <span className="text-xs uppercase tracking-[0.2em] text-[#6b6b6b]">or</span>
          <div className="h-px flex-1 bg-[#27272A]" />
        </div>

        <button
          onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-3 bg-white text-[#1a1a1a] font-medium py-3 rounded-sm hover:bg-[#f1f1f1] transition-colors"
          data-testid="google-login-button"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-xs text-[#6b6b6b] text-center mt-6">Angel Desk is restricted to authorized staff.</p>
      </div>
    </div>
  );
}
